import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  ArrowLeft, ArrowRight, AlertCircle, CheckCircle,
  CreditCard, DollarSign, BookOpen, ShoppingBag,
  Home, Heart, User, FileText, Send, Calendar, Check
} from 'lucide-react';
import { LOAN_CONFIG } from '../../utils/loanConfig';
import { formatCurrency } from '../../utils/helpers';
import { getLoans, getPayments, createLoan } from '../../utils/supabaseAPI';
import { BACKEND_URL } from '../../config/backend';
import { supabase } from '../../utils/supabaseClient';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const NAVY = '#0f2d6b';
const GOLD = '#e8a020';

// ── Shared input style ─────────────────────────────────────────────────────────
const inputCls = (err) =>
  `w-full px-4 py-3 border rounded-xl text-slate-900 placeholder-slate-400 bg-white transition-all focus:outline-none focus:ring-2 focus:border-transparent text-sm ${
    err ? 'border-red-300 bg-red-50 focus:ring-red-300' : 'border-slate-200 focus:ring-[#0f2d6b]/25'
  }`;

const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5';
const errCls   = 'text-red-500 text-xs mt-1';

const LoanRequest = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep]     = useState(1);
  const [formData, setFormData]           = useState({
    amount: '', duration: 5, purpose: '',
    employmentStatus: 'student', guarantee: '',
    momoNumber: '', momoNetwork: '', momoName: '', category: ''
  });
  const [loading, setLoading]             = useState(false);
  const [submitted, setSubmitted]         = useState(false);
  const [loanId, setLoanId]               = useState(null);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [errors, setErrors]               = useState({});
  const [calculation, setCalculation]     = useState(null);
  const [hasActiveLoan, setHasActiveLoan] = useState(false);
  const [checkingLoans, setCheckingLoans] = useState(false);

  const totalSteps = 4;

  // ── All functional logic (unchanged) ────────────────────────────────────────
  useEffect(() => { checkExistingLoans(); /* eslint-disable-next-line */ }, [user?.id]);

  const checkExistingLoans = async () => {
    try {
      setCheckingLoans(true);
      const [loansResult, paymentsResult] = await Promise.all([getLoans(user.id), getPayments(user.id)]);
      if (!loansResult.success) return;
      const userLoans = loansResult.data || [];
      const payments  = paymentsResult.success ? (paymentsResult.data || []) : [];
      const getRemaining = (loan) => {
        const principal = parseFloat(loan.amount) || 0;
        const rate      = parseFloat(loan.interest_rate) || 0;
        const totalDue  = principal * (1 + rate / 100) + (parseFloat(loan.total_penalty_amount) || 0);
        const paid      = payments.filter(p => p.loan_id === loan.id).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        return Math.max(0, totalDue - paid);
      };
      const activeLoan = userLoans.find(loan => {
        if (loan.status !== 'active' && loan.status !== 'approved' && loan.status !== 'overdue') return false;
        return getRemaining(loan) > 0;
      });
      setHasActiveLoan(!!activeLoan);
      if (activeLoan) {
        if (activeLoan.status === 'overdue') showError("Vous ne pouvez pas demander un nouveau prêt tant que votre prêt en retard n'est pas remboursé.");
        else showError('Vous avez déjà un prêt en cours. Vous devez le rembourser avant de faire une nouvelle demande.');
      }
    } catch (e) { console.error('[LOAN_REQUEST]', e); }
    finally { setCheckingLoans(false); }
  };

  useEffect(() => {
    if (formData.amount && formData.duration) calculateLoan();
    /* eslint-disable-next-line */
  }, [formData.amount, formData.duration]);

  const calculateLoan = () => {
    const amount   = parseFloat(formData.amount);
    const duration = parseInt(formData.duration);
    if (!amount || amount < LOAN_CONFIG.amounts.min || amount > LOAN_CONFIG.amounts.max) { setCalculation(null); return; }
    setCalculation({
      principal:    amount,
      interest:     Math.round(LOAN_CONFIG.calculateInterest(amount, duration)),
      totalAmount:  Math.round(LOAN_CONFIG.calculateTotalAmount(amount, duration)),
      interestRate: LOAN_CONFIG.getInterestRate(duration, amount),
      duration
    });
  };

  const loanCategories = [
    { id: 'education', name: 'Éducation',  icon: BookOpen,    bg: '#EFF6FF', color: '#2563EB' },
    { id: 'business',  name: 'Entreprise', icon: ShoppingBag, bg: '#F0FDF4', color: '#16A34A' },
    { id: 'housing',   name: 'Logement',   icon: Home,        bg: '#FFF7ED', color: '#EA580C' },
    { id: 'personal',  name: 'Personnel',  icon: User,        bg: '#F5F3FF', color: '#7C3AED' },
    { id: 'health',    name: 'Santé',      icon: Heart,       bg: '#FDF2F8', color: '#DB2777' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCategorySelect = (id) => {
    setFormData(prev => ({ ...prev, category: id }));
    setErrors(prev => ({ ...prev, category: '' }));
  };

  const validateStep = (step) => {
    const e = {};
    if (step === 1 && !formData.category) e.category = 'Veuillez sélectionner une catégorie';
    if (step === 2) {
      const a = parseFloat(formData.amount);
      if (!a || a < LOAN_CONFIG.amounts.min) e.amount = `Montant minimum : ${formatCurrency(LOAN_CONFIG.amounts.min)}`;
      else if (a > LOAN_CONFIG.amounts.max) e.amount = `Montant maximum : ${formatCurrency(LOAN_CONFIG.amounts.max)}`;
    }
    if (step === 3) {
      if (!formData.purpose || formData.purpose.trim().length < 10) e.purpose = "Décrivez l'utilisation du prêt (min. 10 caractères)";
      if (!formData.guarantee) e.guarantee = 'Veuillez sélectionner une garantie';
    }
    if (step === 4) {
      if (!formData.momoNumber || formData.momoNumber.trim().length < 8) e.momoNumber = 'Numéro Mobile Money invalide';
      if (!formData.momoNetwork) e.momoNetwork = 'Veuillez sélectionner votre opérateur';
      if (!formData.momoName || formData.momoName.trim().length < 3) e.momoName = 'Entrez le nom du compte Mobile Money';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep(p => Math.min(p + 1, totalSteps));
    else showError('Veuillez corriger les erreurs avant de continuer');
  };

  const handlePrevious = () => setCurrentStep(p => Math.max(p - 1, 1));

  const generatePreviewPDF = async () => {
    try {
      setLoading(true);
      if (!supabase) throw new Error('Connexion non disponible');
      const { data: userData, error: userError } = await supabase.from('users').select('*').eq('id', user.id).single();
      if (userError) throw new Error('Impossible de récupérer vos informations');
      const amount = parseFloat(formData.amount), duration = parseInt(formData.duration);
      const response = await fetchWithAuth(`${BACKEND_URL}/api/generate-preview-pdf`, {
        method: 'POST',
        body: JSON.stringify({
          user: { first_name: userData.first_name, last_name: userData.last_name, email: userData.email, phone_number: userData.phone_number, temoin_name: userData.temoin_name, temoin_phone: userData.temoin_phone, temoin_quartier: userData.temoin_quartier, temoin_email: userData.temoin_email },
          loan: { amount, duration, interest_rate: LOAN_CONFIG.getInterestRate(duration, amount), purpose: formData.purpose, guarantee: formData.guarantee, category: formData.category },
          calculation: { interest: Math.round(LOAN_CONFIG.calculateInterest(amount, duration)), totalAmount: Math.round(LOAN_CONFIG.calculateTotalAmount(amount, duration)) }
        })
      });
      if (!response.ok) throw new Error(`Erreur PDF : ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'engagement-pret-preview.pdf';
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setPdfDownloaded(true);
      showSuccess('PDF téléchargé ! Vous pouvez maintenant soumettre votre demande.');
    } catch (err) { showError(err.message || 'Erreur lors de la génération du PDF'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pdfDownloaded) { showError("Veuillez télécharger le PDF d'engagement avant de soumettre"); return; }
    if (!validateStep(currentStep)) { showError('Veuillez corriger les erreurs'); return; }
    if (hasActiveLoan) { showError('Vous avez déjà un prêt en cours'); return; }
    setLoading(true);
    try {
      const amount = parseFloat(formData.amount), duration = parseInt(formData.duration);
      const result = await createLoan({
        user_id: user.id, amount, duration, duration_months: duration,
        interest_rate: LOAN_CONFIG.getInterestRate(duration, amount),
        purpose: formData.purpose, employment_status: formData.employmentStatus,
        guarantee: formData.guarantee, momo_number: formData.momoNumber,
        momo_network: formData.momoNetwork, momo_name: formData.momoName, status: 'pending'
      });
      if (result.success && result.data) { setLoanId(result.data.id); setSubmitted(true); showSuccess('Demande soumise avec succès !'); }
      else throw new Error(result.error || 'Erreur lors de la soumission');
    } catch (err) { showError(err.message || 'Erreur lors de la soumission'); }
    finally { setLoading(false); }
  };

  const handleDownloadPDF = async () => {
    if (!loanId) { showError('ID de prêt manquant'); return; }
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/generate-pdf/${loanId}`);
      if (!response.ok) throw new Error('Erreur lors de la génération du PDF');
      const blob = await response.blob(), url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `engagement-pret-${loanId}.pdf`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setPdfDownloaded(true); showSuccess('PDF téléchargé !');
    } catch (err) { showError('Erreur lors du téléchargement du PDF'); }
  };

  // ── Success state ────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: '#F0FDF4' }}>
            <CheckCircle size={32} style={{ color: '#16A34A' }} />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900 mb-2">Demande envoyée !</h2>
          <p className="text-slate-500 text-sm mb-6">Votre demande est en cours d'examen. Vous serez notifié dans les plus brefs délais.</p>

          {!pdfDownloaded ? (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-5 text-left">
              <div className="flex items-start gap-3">
                <FileText size={20} style={{ color: '#2563EB' }} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900 text-sm mb-1">Document d'engagement</p>
                  <p className="text-blue-700 text-xs mb-3">Téléchargez et signez votre document d'engagement de prêt.</p>
                  <button onClick={handleDownloadPDF}
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-semibold"
                    style={{ background: '#2563EB' }}>
                    <FileText size={15} /> Télécharger le PDF
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mb-5 text-left">
              <div className="flex items-center gap-3">
                <CheckCircle size={18} style={{ color: '#16A34A' }} className="flex-shrink-0" />
                <p className="text-green-800 text-sm font-medium">PDF téléchargé · Signez et conservez le document</p>
              </div>
            </div>
          )}

          <button onClick={() => navigate('/dashboard')}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm text-white"
            style={{ background: NAVY }}>
            <ArrowLeft size={16} /> Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  // ── Steps config ─────────────────────────────────────────────────────────────
  const steps = [
    { number: 1, title: 'Catégorie' },
    { number: 2, title: 'Montant' },
    { number: 3, title: 'Détails' },
    { number: 4, title: 'Paiement' },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-24">

      {/* Checking loans banner */}
      {checkingLoans && (
        <div className="flex items-center justify-center gap-2 py-2 bg-blue-50 text-blue-700 text-xs font-medium">
          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Vérification des prêts en cours…
        </div>
      )}

      {/* ── HERO HEADER (same style as dashboard) ─────────────────────────── */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3f8f 100%)` }}>
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-5 -left-5 w-24 h-24 rounded-full pointer-events-none" style={{ background: `${GOLD}15` }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-4">
          {/* Top row */}
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate('/dashboard')}
              className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0 transition-colors">
              <ArrowLeft size={16} className="text-white" />
            </button>
            <div className="min-w-0">
              <p className="text-white/55 text-[10px] font-medium tracking-wide leading-none mb-0.5">AB Campus Finance</p>
              <h1 className="text-white font-bold text-base leading-tight">
                Demande de prêt
                <span className="ml-2 text-xs font-normal" style={{ color: `${GOLD}cc` }}>
                  Étape {currentStep}/{totalSteps}
                </span>
              </h1>
            </div>
          </div>

          {/* Step progress bar */}
          <div className="flex items-center gap-0">
            {steps.map((s, i) => (
              <React.Fragment key={s.number}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold transition-all duration-300 ${
                    currentStep > s.number  ? 'bg-green-400 text-white' :
                    currentStep === s.number ? 'bg-white text-[#0f2d6b]' :
                    'bg-white/20 text-white/60'
                  }`}>
                    {currentStep > s.number ? <Check size={10} /> : s.number}
                  </div>
                  <span className={`text-[9px] font-semibold tracking-wide whitespace-nowrap ${
                    currentStep >= s.number ? 'text-white' : 'text-white/40'
                  }`}>{s.title}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 mb-4 rounded-full transition-all duration-500 ${
                    currentStep > s.number ? 'bg-green-400' : 'bg-white/20'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT ───────────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4">

        {/* Active loan warning */}
        {hasActiveLoan && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 mb-4">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">Vous avez un prêt en cours. Remboursez-le avant de faire une nouvelle demande.</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* ── Desktop: 2-column layout for step 2 & 4 ── */}
          <div className={`lg:grid lg:gap-6 ${(currentStep === 2 || currentStep === 4) ? 'lg:grid-cols-[1fr_340px]' : ''}`}>

            {/* ── MAIN FORM CARD ───────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6">

              {/* Step 1 — Category */}
              {currentStep === 1 && (
                <div>
                  <p className={labelCls}>Catégorie du prêt</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {loanCategories.map(cat => (
                      <button key={cat.id} type="button" onClick={() => handleCategorySelect(cat.id)}
                        className={`flex flex-col items-center gap-2.5 py-4 px-3 rounded-2xl border-2 transition-all duration-200 ${
                          formData.category === cat.id
                            ? 'border-[#0f2d6b] shadow-md scale-[1.03]'
                            : 'border-slate-100 hover:border-slate-200'
                        }`}
                        style={formData.category === cat.id ? { background: `${NAVY}08` } : {}}>
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: cat.bg }}>
                          <cat.icon size={22} style={{ color: cat.color }} />
                        </div>
                        <span className="text-slate-700 text-xs font-semibold">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                  {errors.category && <p className={errCls}>{errors.category}</p>}
                </div>
              )}

              {/* Step 2 — Amount & duration */}
              {currentStep === 2 && (
                <div className="space-y-5">
                  <div>
                    <label className={labelCls}>Montant souhaité <span className="text-red-400 normal-case tracking-normal font-normal text-[10px]">({formatCurrency(LOAN_CONFIG.amounts.min)} – {formatCurrency(LOAN_CONFIG.amounts.max)})</span></label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="number" name="amount" value={formData.amount} onChange={handleChange}
                        placeholder="Ex : 50 000" min={LOAN_CONFIG.amounts.min} max={LOAN_CONFIG.amounts.max}
                        className={`${inputCls(errors.amount)} pl-10`} />
                    </div>
                    {errors.amount && <p className={errCls}>{errors.amount}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Durée de remboursement</label>
                    <div className="relative">
                      <Calendar size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <select name="duration" value={formData.duration} onChange={handleChange}
                        className={`${inputCls(false)} pl-10 appearance-none`}>
                        {LOAN_CONFIG.durations.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Calculation (mobile only — desktop shows in right panel) */}
                  {calculation && (
                    <div className="lg:hidden rounded-2xl p-4 border border-slate-100" style={{ background: `${NAVY}06` }}>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Récapitulatif</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-500">Montant</span><span className="font-semibold text-slate-900">{formatCurrency(calculation.principal)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Intérêts ({calculation.interestRate}%)</span><span className="font-semibold text-orange-600">+{formatCurrency(calculation.interest)}</span></div>
                        <div className="flex justify-between pt-2 border-t border-slate-200"><span className="font-bold text-slate-900">Total à rembourser</span><span className="font-extrabold text-lg" style={{ color: NAVY }}>{formatCurrency(calculation.totalAmount)}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3 — Details */}
              {currentStep === 3 && (
                <div className="space-y-5">
                  <div>
                    <label className={labelCls}>Utilisation du prêt</label>
                    <textarea name="purpose" value={formData.purpose} onChange={handleChange} rows={4}
                      placeholder="Décrivez précisément l'utilisation du prêt…"
                      className={inputCls(errors.purpose)} />
                    {errors.purpose && <p className={errCls}>{errors.purpose}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Statut professionnel</label>
                    <select name="employmentStatus" value={formData.employmentStatus} onChange={handleChange} className={inputCls(false)}>
                      <option value="student">Étudiant</option>
                      <option value="self-employed">Indépendant</option>
                      <option value="employed">Salarié</option>
                      <option value="unemployed">Sans emploi</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Garantie proposée</label>
                    <select name="guarantee" value={formData.guarantee} onChange={handleChange} className={inputCls(errors.guarantee)}>
                      <option value="">Sélectionnez une garantie</option>
                      <option value="🪪 Carte d'identité / étudiante">🪪 Carte d'identité / étudiante</option>
                      <option value="📱 Téléphone portable">📱 Téléphone portable</option>
                      <option value="💻 Ordinateur portable">💻 Ordinateur portable</option>
                      <option value="🔥 Bouteille de gaz">🔥 Bouteille de gaz</option>
                      <option value="🪙 Montre connectée ou classique de valeur">🪙 Montre de valeur</option>
                      <option value="📷 Tablette">📷 Tablette</option>
                      <option value="🎧 Écouteurs ou casque Bluetooth">🎧 Écouteurs / casque Bluetooth</option>
                      <option value="🧳 Petit ventilateur ou cuisinière électrique">🧳 Ventilateur / cuisinière</option>
                      <option value="💾 Disque dur externe ou clé USB haut de gamme">💾 Disque dur / clé USB</option>
                      <option value="🪑 Petit appareil électroménager (fer à repasser, mixeur, etc.)">🪑 Petit électroménager</option>
                    </select>
                    {errors.guarantee && <p className={errCls}>{errors.guarantee}</p>}
                  </div>
                </div>
              )}

              {/* Step 4 — Mobile Money */}
              {currentStep === 4 && (
                <div className="space-y-5">
                  <div>
                    <label className={labelCls}>Opérateur Mobile Money</label>
                    <select name="momoNetwork" value={formData.momoNetwork} onChange={handleChange} className={inputCls(errors.momoNetwork)}>
                      <option value="">Sélectionnez votre opérateur</option>
                      <option value="MTN">MTN Mobile Money 🟡</option>
                      <option value="Moov">Moov Money 🔵</option>
                      <option value="Celtiis">Celtiis Cash</option>
                    </select>
                    {errors.momoNetwork && <p className={errCls}>{errors.momoNetwork}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Numéro Mobile Money</label>
                    <input type="tel" name="momoNumber" value={formData.momoNumber} onChange={handleChange}
                      placeholder="Ex : 97 12 34 56" className={inputCls(errors.momoNumber)} />
                    {errors.momoNumber && <p className={errCls}>{errors.momoNumber}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Nom du titulaire du compte</label>
                    <input type="text" name="momoName" value={formData.momoName} onChange={handleChange}
                      placeholder="Nom complet" className={inputCls(errors.momoName)} />
                    {errors.momoName && <p className={errCls}>{errors.momoName}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* ── RIGHT PANEL (desktop, step 2 & 4) ────────────────────────── */}
            {(currentStep === 2 || currentStep === 4) && (
              <div className="hidden lg:flex flex-col gap-4">

                {/* Calculation summary */}
                {currentStep === 2 && calculation && (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Simulation</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Montant</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(calculation.principal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Taux</span>
                        <span className="font-semibold text-slate-900">{calculation.interestRate}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Intérêts</span>
                        <span className="font-semibold text-orange-500">+{formatCurrency(calculation.interest)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Durée</span>
                        <span className="font-semibold text-slate-900">{calculation.duration} jours</span>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-slate-800">Total à rembourser</span>
                          <span className="font-extrabold text-xl" style={{ color: NAVY }}>{formatCurrency(calculation.totalAmount)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PDF & info panel (step 4) */}
                {currentStep === 4 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Document requis</p>
                    {!pdfDownloaded ? (
                      <>
                        <p className="text-slate-500 text-sm mb-4">Téléchargez et signez le document d'engagement avant de soumettre.</p>
                        <button type="button" onClick={generatePreviewPDF} disabled={loading}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition-opacity hover:opacity-90"
                          style={{ background: NAVY }}>
                          {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Génération…</> : <><FileText size={16} /> Télécharger le PDF</>}
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-xl p-3">
                        <CheckCircle size={18} style={{ color: '#16A34A' }} className="flex-shrink-0" />
                        <p className="text-green-800 text-sm font-medium">PDF téléchargé ✓ Vous pouvez soumettre.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Info card */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">À savoir</p>
                  <ul className="space-y-2 text-xs text-slate-500">
                    {['Réponse sous 24h ouvrées', 'Virement Mobile Money instantané', '100% sécurisé et confidentiel', 'Aucun frais caché'].map(t => (
                      <li key={t} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* PDF section mobile (step 4 only) */}
          {currentStep === 4 && (
            <div className="lg:hidden mt-4">
              {!pdfDownloaded ? (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <FileText size={18} style={{ color: '#2563EB' }} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900 text-sm mb-1">Document d'engagement requis</p>
                      <p className="text-blue-600 text-xs mb-3">Téléchargez et signez ce document avant de soumettre.</p>
                      <button type="button" onClick={generatePreviewPDF} disabled={loading}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                        style={{ background: '#2563EB' }}>
                        {loading ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Génération…</> : <><FileText size={14} /> Télécharger le PDF</>}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-2xl px-4 py-3">
                  <CheckCircle size={16} style={{ color: '#16A34A' }} className="flex-shrink-0" />
                  <p className="text-green-800 text-sm font-medium">PDF téléchargé · Vous pouvez soumettre</p>
                </div>
              )}
            </div>
          )}

          {/* ── Navigation buttons ─────────────────────────────────────────── */}
          <div className="flex items-center justify-between mt-5 gap-3">
            <button type="button" onClick={handlePrevious} disabled={currentStep === 1}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
              <ArrowLeft size={16} /> Précédent
            </button>

            {currentStep < totalSteps ? (
              <button type="button" onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-opacity hover:opacity-90 active:scale-[0.98]"
                style={{ background: NAVY }}>
                Suivant <ArrowRight size={16} />
              </button>
            ) : (
              <button type="submit" disabled={loading || hasActiveLoan}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-opacity hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: GOLD, color: NAVY }}>
                {loading
                  ? <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Envoi…</>
                  : <><Send size={15} /> Soumettre la demande</>}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoanRequest;
